import { Link, useLocation } from "react-router-dom";
import { useEffect } from "react";

/**
 * SEO-aware 404 page.
 *
 * Why this matters: the SPA shell falls through to this route for any URL the
 * router doesn't recognise. Without a `noindex` signal, Googlebot reads the
 * empty shell as a soft 404 and downranks the whole domain. We set:
 *   - <meta name="robots" content="noindex, follow">  → tell crawlers to skip
 *   - <meta name="prerender-status-code" content="404"> → hint for prerender layers
 *   - <title> 404 — AskThePods → distinct from homepage so Google can detect it
 */
const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);

    const prevTitle = document.title;
    document.title = "404 — Page not found | AskThePods";

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
      el.setAttribute("data-not-found", "true");
    };

    setMeta("robots", "noindex, follow");
    setMeta("prerender-status-code", "404");
    setMeta("googlebot", "noindex, follow");

    return () => {
      document.title = prevTitle;
      // Remove the noindex tags so other routes aren't accidentally noindexed
      document.querySelectorAll('meta[data-not-found="true"]').forEach((el) => el.remove());
    };
  }, [location.pathname]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted">
      <div className="text-center max-w-md px-4">
        <h1 className="mb-4 text-4xl font-bold">404</h1>
        <p className="mb-2 text-xl text-muted-foreground">Page not found</p>
        <p className="mb-6 text-sm text-muted-foreground">
          The page <code className="bg-background px-1 rounded">{location.pathname}</code> doesn't exist.
        </p>
        <div className="flex flex-col sm:flex-row gap-2 justify-center">
          <Link to="/" className="text-primary underline hover:text-primary/90">
            Return to Home
          </Link>
          <span className="hidden sm:inline text-muted-foreground">·</span>
          <Link to="/answers" className="text-primary underline hover:text-primary/90">
            Browse Answers
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotFound;
